// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { StorageAction } from '@aws-amplify/core/internals/utils';
import { calculateContentMd5, resolveS3ConfigAndInput, validateBucketOwnerID, validateStorageOperationInput, } from '../../../utils';
import { putObject } from '../../../utils/client/s3data';
import { getStorageUserAgentValue } from '../../../utils/userAgent';
import { CHECKSUM_ALGORITHM_CRC32, STORAGE_INPUT_KEY, } from '../../../utils/constants';
import { calculateContentCRC32 } from '../../../utils/crc32';
import { constructContentDisposition } from '../../../utils/constructContentDisposition';
import { getContentType } from '../../../../../utils/contentType';
/**
 * Get a function the returns a promise to call putObject API to S3.
 *
 * @internal
 */
export const putObjectJob = (amplify, uploadDataInput, abortSignal, totalLength) => async () => {
    const { options: uploadDataOptions, data } = uploadDataInput;
    const { bucket, keyPrefix, s3Config, isObjectLockEnabled, identityId } = await resolveS3ConfigAndInput(amplify, uploadDataInput);
    const { inputType, objectKey } = validateStorageOperationInput(uploadDataInput, identityId);
    validateBucketOwnerID(uploadDataOptions?.expectedBucketOwner);
    const finalKey = inputType === STORAGE_INPUT_KEY ? keyPrefix + objectKey : objectKey;
    const { contentDisposition, contentEncoding, contentType = uploadDataOptions?.contentType ??
        getContentType(data, objectKey) ??
        'application/octet-stream', preventOverwrite, metadata, checksumAlgorithm, onProgress, expectedBucketOwner, cacheControl, } = uploadDataOptions ?? {};
    const checksumCRC32 = checksumAlgorithm === CHECKSUM_ALGORITHM_CRC32
        ? await calculateContentCRC32(data)
        : undefined;
    const contentMD5 = 
    // check if checksum exists. ex: should not exist in react native
    !checksumCRC32 && isObjectLockEnabled
        ? await calculateContentMd5(data)
        : undefined;
    const { ETag: eTag, VersionId: versionId } = await putObject({
        ...s3Config,
        abortSignal,
        onUploadProgress: onProgress,
        userAgentValue: getStorageUserAgentValue(StorageAction.UploadData),
    }, {
        Bucket: bucket,
        Key: finalKey,
        Body: data,
        ContentType: contentType,
        ContentDisposition: constructContentDisposition(contentDisposition),
        ContentEncoding: contentEncoding,
        CacheControl: cacheControl,
        Metadata: metadata,
        ContentMD5: contentMD5,
        ChecksumCRC32: checksumCRC32,
        ExpectedBucketOwner: expectedBucketOwner,
        IfNoneMatch: preventOverwrite ? '*' : undefined,
    });
    const result = {
        eTag,
        versionId,
        contentType,
        metadata,
        size: totalLength,
    };
    return inputType === STORAGE_INPUT_KEY
        ? { key: objectKey, ...result }
        : { path: objectKey, ...result };
};
